import { LineChart, BarChart, PieChart } from '@mui/x-charts';
import { Box, CircularProgress, Typography } from '@mui/material';
import TradeChart from './TradeChart';
import AssetChart from './AssetChart';
import DebtChart from './DebtChart';
import DualAxisChart from './DualAxisChart';
import ForexTable from './ForexTable';
import { chartConfigs, toYYYYMM } from '../utils/constants';
import USDebtChart from './USDebtChart';
import GDPChart from './GDPChart';
import RareEarthChart from './RareEarthChart';

// 一籃子貨幣市場份額
const pieData = [
  { id: 0, label: 'USD', value: 43.38, color: '#FFD700' },
  { id: 1, label: 'EUR', value: 29.31, color: '#1E90FF' },
  { id: 2, label: 'CNY', value: 12.28, color: '#FF4C4C' },
  { id: 3, label: 'JPY', value: 7.59, color: '#A9A9A9' },
  { id: 4, label: 'GBP', value: 7.44, color: '#32CD32' },
];

export default function ChartRenderer({
  chartType,
  loading,
  data,
  forexData,
  filteredDates,
  dualLineSeries,
  chartData,
  tradeSeries,
  debtSeries,
  tradeMode,
  jobMode,
  range,
  leftAxis,
  rightAxis,
  onAxisChange,
  onFreecodeChange
}) {
  const chartConfig = chartConfigs[chartType];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!chartConfig) return null;

  let chart = null;

  if (chartConfig.type === "line") {
    chart = <AssetChart data={data} filteredDates={filteredDates} range={range} />;

  } else if (chartConfig.type === "dualLine") {
    chart = (
      <LineChart
        height={550} width={1100}
        series={dualLineSeries}
        xAxis={[{ scaleType: 'point', data: filteredDates, tickLabelStyle: { fontSize: 12, angle: -45 } }]}
        yAxis={[
          { id: 'leftAxisId', width: 60, label: chartConfig.leftLabel, labelStyle: { fontSize: 14, fill: '#08dfeeff' } },
          { id: 'rightAxisId', position: 'right', label: chartConfig.rightLabel, labelStyle: { fontSize: 14, fill: '#eea913ff' } }
        ]}
        margin={{ left: 80, right: 80, top: 20, bottom: 80 }}
        grid={{ vertical: true, horizontal: true }}
      />
    );

  } else if (chartConfig.type === "dualBar" && range !== 365 * 100 + 31) {
    chart = (
      <BarChart
        height={550} width={1100}
        series={dualLineSeries}
        xAxis={[{ scaleType: 'band', data: filteredDates, valueFormatter: toYYYYMM, tickLabelStyle: { fontSize: 12, angle: -45 } }]}
        yAxis={[
          { id: 'leftAxisId', width: 60, label: chartConfig.leftLabel, labelStyle: { fontSize: 14, fill: '#082beeff' } },
          { id: 'rightAxisId', position: 'right', label: chartConfig.rightLabel, labelStyle: { fontSize: 14, fill: '#ee1313ff' } }
        ]}
        margin={{ left: 80, right: 80, top: 20, bottom: 80 }}
        grid={{ vertical: true, horizontal: true }}
      />
    );

  } else if (chartConfig.type === "dualBar" && range === 365 * 100 + 31) {
    const chartSeries = [
      { data: chartData?.map(d => d.employment) || [], yAxisId: "leftAxisId", color: "#110b83ff" }
    ];
    if (jobMode === "bigJob") {
      chartSeries.push({ data: chartData?.map(d => d.unemployment) || [], yAxisId: "rightAxisId", color: "#920909ff" });
    }
    const chartYAxis = [{ id: "leftAxisId", width: 60, labelStyle: { fontSize: 14, fill: "#110b83ff" } }];
    if (jobMode === "bigJob") {
      chartYAxis.push({ id: "rightAxisId", position: "right", labelStyle: { fontSize: 14, fill: "#920909ff" } });
    }
    chart = (
      <BarChart
        height={550} width={1100}
        series={chartSeries}
        xAxis={[{ scaleType: "band", data: chartData?.map(d => d.year) || [], tickLabelStyle: { fontSize: 12 } }]}
        yAxis={chartYAxis}
        margin={{ left: 80, right: 80, top: 20, bottom: 80 }}
        grid={{ vertical: true, horizontal: true }}
      />
    );

  } else if (chartConfig.type === "pie") {
    chart = (
      <Box display="flex" justifyContent="center" my={4}>
        <PieChart
          height={500}
          series={[{
            data: pieData.map(d => ({ ...d, percent: d.value })),
            arcLabel: (item) => `${item.label} ${item.value.toFixed(2)}%`,
            arcLabelMinAngle: 10,
            innerRadius: 60, outerRadius: 210, paddingAngle: 2,
          }]}
          colors={pieData.map(d => d.color)}
          slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' } } }}
        />
      </Box>
    );

  } else if (chartConfig.type === "table") {
    chart = <ForexTable forexData={forexData} />;
  }

  if (!chart) {
    if (chartType === "trade") {
      chart = <TradeChart filteredDates={filteredDates} tradeSeries={tradeSeries} tradeMode={tradeMode} range={range} />;
    } else if (chartType === "rareEarth") {
      chart = <RareEarthChart dataSeries={data} range={range} />;
    } else if (chartType === "chinaPBOC") {
      chart = (!Array.isArray(debtSeries) || debtSeries.length === 0)
        ? <Box display="flex" justifyContent="center" alignItems="center" height={400}><Typography color="textSecondary">暫無中國央行資料或資料格式錯誤</Typography></Box>
        : <DebtChart filteredDates={filteredDates || []} debtSeries={debtSeries || []} range={range} />;
    } else if (chartType === "usDebt") {
      chart = <USDebtChart filteredDates={filteredDates || []} debtSeries={debtSeries || []} range={range} />;
    } else if (chartType === "usGDP") {
      chart = <GDPChart filteredDates={filteredDates || []} data={data || []} range={range} />;
    } else if (chartType === "freedom") {
      chart = (
        <div>
          <DualAxisChart
            data={data} filteredDates={filteredDates}
            controlledLeftAxis={leftAxis} controlledRightAxis={rightAxis}
            onAxisChange={onAxisChange} onFreecodeChange={onFreecodeChange}
          />
          {loading && <div>載入中...</div>}
        </div>
      );
    }
  }

  return chart ?? null;
}